/**
 * Shared QR composite generator for CTAs.
 *
 * Smart Admin replacement R2B (audited 2026-05-17). Used by both
 * eventCTA.service and organizationCTA.service so the overlay logic
 * lives in one place per audit must-fix ("If org CTAs have a separate
 * generation path, the overlay code should be shared rather than
 * copied").
 *
 * Heather's requirement (PDF page 4): the QR needs to be
 * "screenshot-friendly" and "user friendly for showing their name and
 * organization on the QR code." A composite SVG asset (QR + name + org
 * label below) gives operators a single image they can screenshot or
 * print, with their identity visible so they can find their own QR
 * quickly among many.
 *
 * Per audit verdict Decision 3: SVG composite with careful XML
 * escaping. Decision 4: caller decides regeneration trigger (smart-skip
 * is implemented at the call site, not here). Decision 7: no
 * denormalization; the overlay is purely cosmetic on the QR image.
 */

import QRCode from "qrcode";

/** Strictly escape user-supplied text for safe SVG <text> insertion. */
function escapeSvgText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** Truncate to a max width with ellipsis. Per audit must-fix: "cap line length". */
function truncate(text: string, max: number): string {
  if (text.length <= max) {
    return text;
  }
  return text.slice(0, max - 1) + "…";
}

export interface CompositeQRInput {
  /** The URL encoded into the QR code. */
  url: string;
  /**
   * Rep display name. When null/empty, the rep label line is omitted
   * (don't render "Unassigned Rep" — keep the visual minimal per the
   * "QR stays the focal point" intent).
   */
  repName: string | null | undefined;
  /**
   * Secondary label — typically the organization name for event CTAs
   * (so an operator can identify "Britney @ Nick's Guns" at a glance)
   * or the event/CTA name for org CTAs. Required.
   */
  contextLabel: string;
  /** QR module width in pixels. Defaults to 300 (matches prior solo QR). */
  qrSize?: number;
}

const MAX_LABEL_CHARS = 32;

/**
 * Generate a composite SVG containing the QR code and identity labels.
 * Output is a standalone SVG string ready to upload as a single asset.
 */
export async function generateCompositeQRSVG(
  input: CompositeQRInput,
): Promise<string> {
  const qrSize = input.qrSize ?? 300;
  const margin = 16;
  const lineHeight = 22;
  const repName = (input.repName ?? "").trim();
  const contextLabel = (input.contextLabel ?? "").trim();
  const hasRep = repName.length > 0;
  const hasContext = contextLabel.length > 0;
  // Lines below the QR
  const labelLines = [
    hasRep ? truncate(repName, MAX_LABEL_CHARS) : null,
    hasContext ? truncate(contextLabel, MAX_LABEL_CHARS) : null,
  ].filter((s): s is string => !!s);

  const labelBlockHeight =
    labelLines.length > 0 ? labelLines.length * lineHeight + margin : 0;

  const totalWidth = qrSize + margin * 2;
  const totalHeight = qrSize + margin * 2 + labelBlockHeight;

  // Have qrcode emit the QR as plain SVG fragment (no width/height
  // attributes, so we can size via viewBox in the parent).
  const qrSvgRaw = await QRCode.toString(input.url, {
    type: "svg",
    width: qrSize,
    margin: 0,
    color: { dark: "#000000", light: "#FFFFFF" },
  });

  // Pull just the inner <path>s out of qrcode's SVG so we can re-anchor
  // them in our composite. qrcode emits paths in module-unit coordinates
  // (the QR matrix size — typically 21..177 modules across), with the
  // outer <svg> carrying a viewBox="0 0 N N" that maps those units to
  // pixels. We MUST preserve that original viewBox when re-wrapping —
  // otherwise paths in 0..N coordinates render inside a 0..qrSize
  // viewport and the QR becomes a tiny corner sliver of the canvas.
  // Regression caught in prod 2026-05-18 (Codex/Claude initial pass
  // only addressed the CSS bounding box, not this root cause).
  const viewBoxMatch = qrSvgRaw.match(/viewBox="([^"]+)"/);
  const qrViewBox = viewBoxMatch ? viewBoxMatch[1] : `0 0 ${qrSize} ${qrSize}`;
  const innerMatch = qrSvgRaw.match(/<svg[^>]*>([\s\S]*?)<\/svg>/);
  const qrInner = innerMatch ? innerMatch[1] : qrSvgRaw;

  const labelStartY = margin + qrSize + margin;
  const labelTextElements = labelLines
    .map(
      (line, i) =>
        `  <text x="${totalWidth / 2}" y="${
          labelStartY + i * lineHeight
        }" text-anchor="middle" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" font-size="${
          i === 0 ? 16 : 14
        }" font-weight="${i === 0 ? "600" : "400"}" fill="#111827">${escapeSvgText(line)}</text>`,
    )
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}">
  <rect width="${totalWidth}" height="${totalHeight}" fill="#FFFFFF"/>
  <g transform="translate(${margin}, ${margin})">
    <svg width="${qrSize}" height="${qrSize}" viewBox="${qrViewBox}">${qrInner}</svg>
  </g>
${labelTextElements}
</svg>`;
}
